import _get from 'lodash/get'
import { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactLocationDevtools } from 'react-location-devtools'
import { Router, ReactLocation, Navigate, Outlet, Route, RouteMatch } from 'react-location'

import { LocationGenerics } from './types'
import { isProduction } from './helpers/common'
import { useSnackbar } from './components/containers/SnackbarContainer'

import Login from './routes/Login'
import Auth from './routes/Auth/Auth'
import NotFound from './routes/NotFound'
import Loading from './components/layouts/Loading'
import { useDealByIdFetch } from './stores/deals-store'
import PublicRouteRPC from './components/render/PublicRouteRPC'
import PrivateRouteRPC from './components/render/PrivateRouteRPC'
import { useRawProspectByIdFetch } from './stores/prospects-store'
import { useOrganizationByIdFetch } from './stores/organizations-store'

const Deals = lazy(() => import(/* webpackPrefetch: true */ './routes/Auth/Deals/Deals'))
const DealsProfile = lazy(() => import(/* webpackPrefetch: true */ './routes/Auth/Deals/{dealId}/DealsProfile'))
const Prospects = lazy(() => import(/* webpackPrefetch: true */ './routes/Auth/Prospects/Prospects'))
const ProspectProfile = lazy(
  () => import(/* webpackPrefetch: true */ './routes/Auth/Prospects/{prospectId}/ProspectProfile')
)
const Organizations = lazy(() => import(/* webpackPrefetch: true */ './routes/Auth/Organizations/Organizations'))
const OrganizationProfile = lazy(
  () => import(/* webpackPrefetch: true */ './routes/Auth/Organizations/{organizationId}/OrganizationProfile')
)

const Tasks = lazy(() => import(/* webpackPrefetch: true */ './routes/Auth/Tasks/Tasks'))

const location = new ReactLocation<LocationGenerics>()
const breadcrumb = (routeMatch: RouteMatch<LocationGenerics>): unknown => {
  const route = routeMatch.route
  if (route.path?.startsWith(':')) return Object.values(routeMatch.params).at(0)
  return route.path
}

function AppRoutes(): JSX.Element {
  const { t } = useTranslation()
  const { openErrorSnackbar } = useSnackbar()
  const fetchRawProspect = useRawProspectByIdFetch()
  const fetchDeal = useDealByIdFetch()
  const fetchOrganization = useOrganizationByIdFetch()

  const routes: Route<LocationGenerics>[] = [
    {
      path: '/',
      element: <Navigate to="prospects" />,
    },
    {
      path: 'prospects',
      meta: { breadcrumb },
      children: [
        { path: '/', element: <PrivateRouteRPC render={() => <Prospects />} /> },
        {
          path: ':prospectId',
          element: <PrivateRouteRPC render={() => <ProspectProfile />} />,
          loader: async ({ params }) => {
            const res = { prospect: {} }

            try {
              res.prospect = await fetchRawProspect(params.prospectId)
            } catch (err) {
              openErrorSnackbar(err, t('failedToGetProspect'))
            }

            return Promise.resolve(res)
          },
          pendingElement: <Loading />,
          pendingMs: 1,
          meta: { breadcrumb: (routeMatch) => routeMatch.data.prospect?.fullName },
        },
      ],
    },
    {
      path: 'organizations',
      meta: { breadcrumb },
      children: [
        { path: '/', element: <PrivateRouteRPC render={() => <Organizations />} /> },
        {
          path: ':organizationId',
          element: <PrivateRouteRPC render={() => <OrganizationProfile />} />,
          loader: async ({ params }) => {
            const res = { organization: {} }

            try {
              res.organization = await fetchOrganization(params.organizationId)
            } catch (err) {
              openErrorSnackbar(err, t('failedToGetOrganization'))
            }

            return Promise.resolve(res)
          },
          pendingElement: <Loading />,
          pendingMs: 1,
          meta: { breadcrumb: (routeMatch) => routeMatch.data.organization?.name },
        },
      ],
    },
    {
      path: 'deals',
      meta: { breadcrumb: () => 'deals' },
      children: [
        { path: '/', element: <PrivateRouteRPC render={() => <Deals />} /> },
        {
          path: ':dealId',
          element: <PrivateRouteRPC render={() => <DealsProfile />} />,
          loader: async ({ params }) => {
            const res = { deal: {} }

            try {
              res.deal = await fetchDeal(params.dealId)
            } catch (err) {
              openErrorSnackbar(err, t('failedToGetDeal'))
            }

            return Promise.resolve(res)
          },
          pendingElement: <Loading />,
          pendingMs: 1,
          meta: { breadcrumb: (routeMatch) => _get(routeMatch, 'data.deal.name.value') },
        },
      ],
    },
    {
      path: 'tasks',
      element: <PrivateRouteRPC render={() => <Tasks />} />,
      meta: { breadcrumb: () => 'tasks' },
    },

    { path: 'login', element: <PublicRouteRPC render={() => <Login />} /> },
    { path: '*', element: <NotFound /> },
  ]

  return (
    <Suspense fallback={<Loading />}>
      <Router location={location} routes={routes}>
        <Auth>
          <Suspense fallback={<Loading />}>
            <Outlet />
            {!isProduction() ? <ReactLocationDevtools initialIsOpen={false} position="bottom-right" /> : <></>}
          </Suspense>
        </Auth>
      </Router>
    </Suspense>
  )
}

export default AppRoutes
